/**
 * THE HELP ASSISTANT'S PRODUCT CONSTANTS — plan-019 DOM (plan-019 §2.6, OD-11; design §31.14.1; ADRs 0065–0068,
 * proposed).
 *
 * Owner-decided figures: never configuration, never measured, never defaulted somewhere else. A refusal tells the person
 * when they can try again, never the figure (design §31.14.1). Technical values (the idle limits, the lease, the frame
 * size, the help margin and H_max) are configuration (`packages/config/src/help.ts`); the help weight's token figures are
 * measured constants (`packages/measured-constants/src/help.ts`).
 *
 * Deliberately absent, by the owner's answers at AP-0 (plan-019 §7, 2026-10-15):
 *
 * - **OD-18 "backup model no limits".** There is no help-specific cap on help turns placed on the model backup (the
 *   default's sub-ceiling of 4 is dropped). The backup's own deployment quota and customers-first still bound it — help
 *   never takes backup room a customer conversation needs — and that is the admission structure's rule, not a figure here.
 * - **OD-8 "keep for ever".** No retention period exists for help transcripts or help requests: nothing here is a number
 *   of days or months after which a help record is deleted. Erasure on a person's request, an operator's erasure, person
 *   export and delete and organization closure remain (design §31.12, minus its two time-based rows).
 *
 * Imports nothing, so the widget may read the few it shows (the message length).
 */
// ── one message and one conversation (OD-11) ────────────────────────────────────────────────
/** A person's message is 1..this many characters after trimming (widget, api, runtime). */
export const HELP_MESSAGE_CHAR_LIMIT = 1_000;
/** Model turns per help conversation; the conversation closes `turn_limit` after the last answer. */
export const HELP_MODEL_TURN_LIMIT = 40;
/** At most this many open help conversations platform-wide (`{conv}:help:open`). */
export const HELP_OPEN_CONVERSATION_LIMIT = 20;
/** The `ready` frame replays at most this many turns on a resume. */
export const HELP_HISTORY_TURN_LIMIT = 40;
// ── anonymous and per-person rates (OD-11, SEC-HA-3, design §31.14.1) ───────────────────────
/** Messages an hour per anonymous visitor key. */
export const HELP_VISITOR_MESSAGE_HOURLY_LIMIT = 30;
/** Messages a day per anonymous visitor key. */
export const HELP_VISITOR_MESSAGE_DAILY_LIMIT = 100;
/** Messages an hour per network prefix (IPv4 /24, IPv6 /48). */
export const HELP_PREFIX_MESSAGE_HOURLY_LIMIT = 300;
/** Messages an hour per signed-in person (SEC-HA-3). */
export const HELP_PERSON_MESSAGE_HOURLY_LIMIT = 60;
/** Help grants an hour per anonymous visitor key. */
export const HELP_VISITOR_GRANT_HOURLY_LIMIT = 30;
/** Help grants an hour per network prefix. */
export const HELP_PREFIX_GRANT_HOURLY_LIMIT = 300;
/** Anonymous help requests a day per visitor key. */
export const HELP_VISITOR_REQUEST_DAILY_LIMIT = 5;
/** Anonymous help requests a day per network prefix. */
export const HELP_PREFIX_REQUEST_DAILY_LIMIT = 20;
/** Help requests a day per signed-in person (added by design §31.0.2). */
export const HELP_PERSON_REQUEST_DAILY_LIMIT = 20;
/** Help sockets opened an hour per network address, checked before the upgrade (added by design §31.0.2). */
export const HELP_ADDRESS_SOCKET_OPEN_HOURLY_LIMIT = 60;
/**
 * Model tokens a day for every help turn together (IQ-7: 20 million; owner 2026-10-15: 10 million). Reached → answers stop with "I can't answer right
 * now, leave a message", messages still work, and `help_budget.reached` alerts once that day.
 */
export const HELP_DAILY_TOKEN_BUDGET = 10_000_000;
// ── one answer (FR-HA-8, design §31.6) ──────────────────────────────────────────────────────
/** At most this many links in `reply_end`. */
export const HELP_ANSWER_LINK_LIMIT = 3;
/** At most this many passages in one turn's INFORMATION block. */
export const HELP_TURN_PASSAGE_LIMIT = 5;
/** At most this many fields in one `fill_form` proposal. */
export const HELP_PROPOSAL_FIELD_LIMIT = 20;
/** An `offer` frame's summary is 1..this many characters. */
export const HELP_OFFER_SUMMARY_CHAR_LIMIT = 300;
/** A `page_context` frame is at most this many bytes (UTF-8) as sent. */
export const HELP_PAGE_CONTEXT_BYTE_LIMIT = 8_192;
// ── help requests and operator text (design §31.3.2) ────────────────────────────────────────
/** A help request's text is 1..this many characters. */
export const HELP_REQUEST_TEXT_CHAR_LIMIT = 1_000;
/** An operator's reply to a help request. */
export const HELP_REPLY_TEXT_CHAR_LIMIT = 4_000;
/** An operator's internal note on a help request. */
export const HELP_NOTE_TEXT_CHAR_LIMIT = 4_000;
/** One operator line in a held help conversation. */
export const HELP_OPERATOR_LINE_CHAR_LIMIT = 2_000;
/** The OD-15 display name snapshot ("Nikos"). */
export const HELP_DISPLAY_NAME_CHAR_LIMIT = 60;
// ── waiting for a person and takeover (FR-HA-26, FR-HA-29, OD-2) ────────────────────────────
/** A person waits this long for an operator to join before `no_person {no_join}` offers the form (FR-HA-26). */
export const HELP_PERSON_WAIT_MS = 180_000;
/**
 * The takeover disconnect (FR-HA-29): a holder's console silent this long ends the hold `lapsed`. It is the upper bound
 * of the configured lease (`WETALK_RUNTIME_HELP_TAKEOVER_LEASE_MS`), never a substitute for it.
 */
export const HELP_TAKEOVER_DISCONNECT_MS = 60_000;
/** An operator with no console input for this long is switched unavailable (OD-2). */
export const HELP_OPERATOR_INACTIVITY_MS = 1_800_000;
// ── shares of the open help conversations (plan-019 review fix, 2026-10-15; owner-tunable, plan-019 §7) ──────
//
// `HELP_OPEN_CONVERSATION_LIMIT` is one platform-wide figure; without shares one address could hold all 20 open help
// conversations for as long as its sockets beat. `HELP_OPEN_SCRIPT` applies these inside the same atomic step:
/** Open help conversations one signed-in person may hold at once. */
export const HELP_OPEN_CONVERSATION_PER_PERSON_LIMIT = 3;
/** Open help conversations one anonymous visitor key may hold at once. */
export const HELP_OPEN_CONVERSATION_PER_VISITOR_LIMIT = 3;
/** Open anonymous help conversations one network prefix (IPv4 /24, IPv6 /48 — the api's digest) may hold at once. */
export const HELP_OPEN_CONVERSATION_PER_PREFIX_LIMIT = 5;
/**
 * Open help conversations reserved for signed-in people: anonymous visitors together hold at most
 * `HELP_OPEN_CONVERSATION_LIMIT − this` (12 of 20).
 */
export const HELP_OPEN_CONVERSATION_SIGNED_IN_RESERVE = 8;
/**
 * Signed-in help grants per person, per UTC hour (design §31.2.3 "per-person grant counter"; plan-019 REVIEW-FIX): the
 * same figure as an anonymous visitor key's, so a person reloading pages is never refused, and a script holding a
 * person's auth_session cannot mint grants without bound. Key `help_count:grant:person:{person_id}:{hour}`.
 */
export const HELP_PERSON_GRANT_HOURLY_LIMIT = 30;
// ── account reads and account actions (plan-025, design §36.7; ADR 0077) ─────────────────────────
/** A read result the help host hands the model is at most this many characters, cut by whole list items. */
export const HELP_ACCOUNT_READ_RESULT_CHAR_LIMIT = 6_000;
/** `read_account` calls answered in one help turn; more are refused `limit_reached`. */
export const HELP_ACCOUNT_READ_PER_TURN_LIMIT = 6;
/** `read_account` calls one person may make an hour (`help_account_read:{person_id}:{hour}`). */
export const HELP_ACCOUNT_READ_PER_PERSON_HOURLY_LIMIT = 120;
/** How deep a help turn that may read the account continues: list → detail → answer (the shared rule's 2 elsewhere). */
export const HELP_ACCOUNT_CONTINUATION_DEPTH = 3;
/** `propose_account_action` cards one person may be shown an hour. */
export const HELP_ACCOUNT_ACTION_PER_PERSON_HOURLY_LIMIT = 10;
/** A confirm card's Confirm button arms this long after the card is shown. */
export const HELP_ACCOUNT_ACTION_ARM_MS = 750;
/** The api keeps an account action's outcome this long after it decided it, for the help host to read. */
export const HELP_ACCOUNT_ACTION_RESULT_KEEP_MS = 600_000;
//# sourceMappingURL=limit.js.map